<template>
  <div
    id="trade-history-container"
    class="trade-history-container"
  >
    <div class="trade-history-container--item">
      <Header/>
    </div>
    <div class="section-gray">
      <div class="trade-history-container--content">
        <div class="trade-history-title">
          {{ $t('trade_history.title') }}
        </div>
        <div class="section-white">
          <div class="columns">
            <div class="column is-one-fofth">
              <b-field :label="$t('trade_history.start_date')">
                <b-datepicker
                  v-model="customStartDate"
                  placeholder="Select"
                  icon="calendar-today"
                  trap-focus
                />
              </b-field>
            </div>
            <div class="column is-one-fifth">
              <b-field :label="$t('trade_history.end_data')">
                <b-datepicker
                  v-model="customEndDate"
                  placeholder="Select"
                  icon="calendar-today"
                  trap-focus
                />
              </b-field>
            </div>
            <div class="column is-one-fifth">
              <b-field :label="$t('trade_history.from_token')">
                <b-select
                  v-model="fromTokenInput"
                  placeholder="Select from token"
                  expanded
                >
                  <option
                    v-for="token in tokensList"
                    :value="token.code"
                    :key="token.code"
                  >
                    {{ token.code }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-one-fifth">
              <b-field :label="$t('trade_history.to_token')">
                <b-select
                  v-model="toTokenInput"
                  placeholder="Select to token"
                  expanded
                >
                  <option
                    v-for="token in tokensList"
                    :value="token.code"
                    :key="token.code"
                  >
                    {{ token.code }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column btn-search-wrap">
              <b-button
                :loading="idSearchLoading"
                class="btn-search is-fullwidth"
                type="is-info"
                @click="fetchTradeHistories"
              >
                {{ $t('trade_history.search') }}
              </b-button>
            </div>
          </div>
        </div>
        <div
          id="btn-download-style"
          class="download-section"
        >
          <b-button
            :disabled="isDownloadDisabled"
            :loading="isDownloadLoading"
            class="btn-download"
            size="is-medium"
            icon-left="download"
            @click="downloadFile"
          >
            {{ $t('trade_history.download') }}
          </b-button>
        </div>
        <b-table
          ref="transactionTable"
          :per-page="paginatedItemPerPage"
          :data="transactions"
          :total="transactions.length"
          class="list-table"
          aria-next-label="Next page"
          aria-previous-label="Previous page"
          aria-page-label="Page"
          aria-current-label="Current page"
          sort-icon="arrow-up"
          hoverable
          paginated
          backend-pagination
          @page-change="(page) => onPageChange(page)"
        >
          <template slot-scope="props">
            <b-table-column
              :label="$t('trade_history.table_column_date')"
              field="date"
              width="180"
              centered
            >
              {{ props.row.date }}
            </b-table-column>
            <b-table-column
              :label="$t('trade_history.table_column_from_token')"
              field="fromToken"
              numeric
            >
              <div class="from-token-wrap">
                <img
                  :src="`/static/token/normal/${props.row.fromToken.symbol.toLowerCase()}.svg`"
                  width="20"
                  height="20"
                >
                <div class="from-token-wrap--amount">
                  - {{ props.row.fromToken.amount | numberWithComma }} {{ props.row.fromToken.symbol }}
                </div>
              </div>
            </b-table-column>
            <b-table-column
              :label="$t('trade_history.table_column_to_token')"
              field="toToken"
              numeric
            >
              <div class="to-token-wrap">
                <img
                  :src="`/static/token/normal/${props.row.toToken.symbol.toLowerCase()}.svg`"
                  width="20"
                  height="20"
                >
                <div class="to-token-wrap--amount">
                  + {{ props.row.toToken.amount | numberWithComma }} {{ props.row.toToken.symbol }}
                </div>
              </div>
            </b-table-column>
            <b-table-column
              :label="$t('trade_history.table_column_value')"
              field="Value"
              width="150"
              numeric
            >
              {{ props.row.value | toFixedDecimal(2) }} THB
            </b-table-column>
            <b-table-column
              :label="$t('trade_history.table_column_transactions')"
              field="transaction"
              centered
            >
              <a
                :href="`${etherScanUrl}/tx/${props.row.transaction}`"
                target="_blank"
              >
                {{ props.row.transaction | shortenHex }}
              </a>
            </b-table-column>
          </template>
          <template slot="empty">
            <section class="section">
              <div class="content has-text-grey has-text-centered">
                <img src="/static/svg/trade-empty.svg">
                <p class="empty-trade-title">
                  {{ $t('trade_history.empty_trade') }}
                </p>
              </div>
            </section>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { saveAs } from 'file-saver'
import csvtojson from 'csvtojson'
import { mapState } from 'vuex'
import Header from '@/features/Header'
import Footer from '@/components/Footer'
import cookie from '@/utils/cookie'
import { kulapApi, awsS3Api } from '@/resources'
import { getEterScanUrl, getCorrectNetwork } from '@/environments'
import TokensList from '@/constants/tokensInfo'
import { convertDatetoUTCStartOfDay, convertDatetoUTCEndOfDay, timestampToDate, showToastError } from '@/utils/helper'
import Service from './service.js'

export default {
  name: 'TradeHistory',
  components: {
    Header,
    Footer
  },
  data () {
    return {
      fromTokenInput: 'ALL',
      toTokenInput: 'ALL',
      tokensList: Object.assign(
        { ALL: { code: 'ALL' } },
        TokensList.main
      ),
      timeRangesInput: { startDate: moment().startOf('month').toDate(), endDate: moment().endOf('month').toDate() },
      lastEvaluatedKey: null,
      paginatedItemPerPage: 20,
      isDownloadLoading: false,
      idSearchLoading: false,
      transactions: [],
      network: getCorrectNetwork()
    }
  },
  computed: {
    ...mapState({
      user: state => state.user
    }),

    customStartDate: {
      get: function () {
        return new Date(this.timeRangesInput.startDate)
      },
      set: function (dateTime) {
        this.timeRangesInput.startDate = convertDatetoUTCStartOfDay(dateTime)
      }
    },
    customEndDate: {
      get: function () {
        return new Date(this.timeRangesInput.endDate)
      },
      set: function (dateTime) {
        this.timeRangesInput.endDate = convertDatetoUTCEndOfDay(dateTime)
      }
    },

    isDownloadDisabled () {
      return this.transactions.length === 0
    },

    etherScanUrl () {
      return getEterScanUrl(this.network)
    }
  },

  async created () {
    if (!cookie.get('user') || !cookie.get('accessToken')) {
      this.$router.push({ name: 'Login', query: { next: 'TradeHistory' } })
      return
    }
    this.service = new Service({
      store: this.$store,
      kulapApi,
      awsS3Api
    })
    this.fetchTradeHistories()
  },
  methods: {
    async fetchTradeHistories () {
      this.idSearchLoading = true
      this.lastEvaluatedKey = null
      try {
        let params = {
          startTimestamp: moment(this.timeRangesInput.startDate).unix(),
          endTimestamp: moment(this.timeRangesInput.endDate).unix(),
          limit: this.paginatedItemPerPage * 2
        }
        if (this.fromTokenInput !== 'ALL') {
          params.fromToken = this.fromTokenInput
        }
        if (this.toTokenInput !== 'ALL') {
          params.toToken = this.toTokenInput
        }
        const { data } = await this.service.getTradeHistories(params)
        this.lastEvaluatedKey = data.lastEvaluatedKey
        this.transactions = data.results.map(data => {
          return {
            date: timestampToDate(data.blockTimestamp).format('YYYY/MM/DD hh:mm'),
            fromToken: {
              symbol: data.fromToken,
              amount: data.srcAmount
            },
            toToken: {
              symbol: data.toToken,
              amount: data.destAmount
            },
            value: data.toTokenThbValue,
            transaction: data.transactionHash
          }
        })
      } catch (error) {
        showToastError(error)
      }
      this.idSearchLoading = false
    },

    async downloadFile () {
      this.isDownloadLoading = true
      try {
        let params = {
          startTimestamp: moment(this.timeRangesInput.startDate).unix(),
          endTimestamp: moment(this.timeRangesInput.endDate).unix()
        }
        if (this.fromTokenInput !== 'ALL') {
          params.fromToken = this.fromTokenInput
        }
        if (this.toTokenInput !== 'ALL') {
          params.toToken = this.toTokenInput
        }
        const { data: tradeHistoriesCsv } = await this.service.getTradeHistoriesFile(params)
        const tradeHistoriesObj = await csvtojson().fromString(tradeHistoriesCsv)
        const fullNameOfUser = tradeHistoriesObj[0]['Full Name'].replace(/ /g, '_')
        const fileName = `${fullNameOfUser}_TX_${moment().format('YYYY-MM-DDThh:mm')}.csv`
        const tradeHistoriesFile = new File([tradeHistoriesCsv], fileName, { type: 'text/csv;charset=utf-8;' })

        saveAs(tradeHistoriesFile)
      } catch (error) {
        showToastError(error)
      }
      this.isDownloadLoading = false
    },

    async onPageChange (newPage) {
      const isLastPage = parseInt(this.transactions.length / this.paginatedItemPerPage) === newPage
      if (isLastPage && this.lastEvaluatedKey) {
        let params = {
          startTimestamp: moment(this.timeRangesInput.startDate).unix(),
          endTimestamp: moment(this.timeRangesInput.endDate).unix(),
          limit: this.paginatedItemPerPage
        }
        if (this.fromTokenInput !== 'ALL') {
          params.fromToken = this.fromTokenInput
        }
        if (this.toTokenInput !== 'ALL') {
          params.toToken = this.toTokenInput
        }
        const { data } = await this.service.getTradeHistories({
          ...params,
          exclusiveStartKey: this.lastEvaluatedKey
        })
        data.results = data.results.map(data => {
          return {
            date: timestampToDate(data.blockTimestamp).format('YYYY/MM/DD hh:mm'),
            fromToken: {
              symbol: data.fromToken,
              amount: data.srcAmount
            },
            toToken: {
              symbol: data.toToken,
              amount: data.destAmount
            },
            value: data.toTokenThbValue,
            transaction: data.transactionHash
          }
        })
        this.transactions = this.transactions.concat(data.results)
        this.lastEvaluatedKey = data.lastEvaluatedKey
      }
    }
  }
}
</script>

<style lang="scss">
#trade-history-container {
  .list-table {
    box-shadow: 0px 0px 6px -2px rgba(0, 0, 0, 0.5);
    max-width: 1024px;
    width: calc(100% - 20px);
    margin: 0 auto;

    .th-wrap {
      color: white;
      font-size: 13px;
    }
    tr > td {
      font-size: 14px;
    }
  }

}
#btn-download-style {
  .button.is-loading::after {
    border-color: transparent transparent #4160A8 #4160A8 !important;
  }
  .button:hover {
    color: #4160A8;
  }
}
</style>

<style lang="scss" scoped>
.trade-history-container {
  float: left;
  width: 100%;
  min-height: 100vh;

  &--item {
    float: left;
    width: 100%;
  }
  &--content {
    max-width: 1024px;
    margin: 0 auto;
  }
}

.section-gray {
  float: left;
  width: 100%;
  padding-bottom: 80px;
}

.trade-history-title {
  margin: 35px 0;
  font-size: 30px;
  font-weight: bold;
  text-align: left;
  color: #000000;
}

.section-white {
  display: block;
  margin: 0 auto;
  padding: 50px;
  max-width: 1024px;
  width: calc(100% - 20px);
  border-radius: 3px;
  box-shadow: 0 0 6px -2px rgba(0, 0, 0, 0.5);
  background-color: #ffffff;
}

.btn-search-wrap {
  display: flex;
  align-items: flex-end;
}
.btn-search {
  background: #4160A8 !important;
  font-weight: 700 !important;
  font-size: 15px !important;
}

.download-section {
  display: flex;
  justify-content: end;
  padding: 20px 0;

  max-width: 1024px;
  width: calc(100% - 12px);
}

.btn-download {
  background: #FFFFFF !important;
  box-shadow: 0px 0px 6px -2px rgba(0, 0, 0, 0.5) !important;
  border-radius: 7.5px;
  height: 40px;

  font-weight: 700;
  font-size: 15px !important;
  color: #4160A8
}

.from-token-wrap {
  display: flex;
  align-items: center;
  // width: 150px;
  margin-left: auto;
  justify-content: flex-end;

  &--amount {
    margin-left: 6px;
    color: #FB6060;
    // width: 100%;
    word-wrap: break-word;
  }
}

.to-token-wrap {
  display: flex;
  align-items: center;
  // width: 150px;
  margin-left: auto;
  justify-content: flex-end;

  &--amount {
    margin-left: 6px;
    color: #18B2A1;
    // width: 100%;
    word-wrap: break-word;
  }
}

.empty-trade-title {
  font-size: 25px;
  font-weight: 500;
}

@media only screen and (max-width: 768px) {
  .from-token-wrap {
    width: 100%;
    justify-content: end;
    &--amount {
      width: unset;
    }
  }
  .to-token-wrap {
    width: 100%;
    justify-content: end;
    &--amount {
      width: unset;
    }
  }

  .trade-history-title {
    text-align: center;
  }

  .download-section {
    padding: 20px 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/TradeHistory/index.vue